import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { Checkbox, Divider, Empty, Input, Popover, Radio, Tooltip } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';

interface SkuInfo {
  skuId: string;
  skuName?: string;
  sellerSku: string;
}

export interface ApplyProductProps {
  imageUrl: string;
  skuInfo?: SkuInfo[];
  defaultApplyType?: 'link' | 'sku';
  skuSelected: string[];
  selectable: boolean;
  onSKUChange: (sku: string[]) => void;
  onClick?: () => void;
  showImage?: boolean;
  size?: 'small' | 'medium';
  v2?: boolean;
  onHide?: (applyType: 'link' | 'sku') => void;
}

interface ApplyAllV2Props {
  active: boolean;
  onClick: () => void;
  skuInfo?: SkuInfo[];
}

const SKUGrid = (props: {
  skuInfo: SkuInfo[];
  skuSelected?: string[];
  onSKUChange: (sku: string[]) => void;
}) => {
  const { skuInfo = [], skuSelected = [], onSKUChange } = props;
  const { t } = useTranslate();
  const [searchKeyword, setSearchKeyword] = useState('');

  const filteredSkuInfo = useMemo(() => {
    return skuInfo.filter((sku) => {
      return sku.sellerSku?.includes(searchKeyword) || sku.skuName?.includes(searchKeyword);
    });
  }, [searchKeyword, skuInfo]);

  return (
    <div>
      <div className="p-1">
        <Input.Search
          size="small"
          placeholder={t('请输入SKU货号、名称进行搜索')}
          value={searchKeyword}
          onChange={(e) => {
            setSearchKeyword(e.target.value);
          }}
          onSearch={(value) => {
            setSearchKeyword(value);
          }}
        />
      </div>
      <div className="min-h-[80px] max-h-[200px] overflow-y-auto">
        {filteredSkuInfo.length === 0 && <Empty description={t('暂无数据')} />}
        {filteredSkuInfo.map((sku) => {
          const text = `${sku.sellerSku || '-'}${sku.skuName ? `(${sku.skuName})` : ''}`;
          return (
            <>
              <div
                key={sku.skuId}
                className="flex items-center px-[4px] gap-[4px] h-[30px] text-[12px] cursor-pointer"
              >
                <Checkbox
                  className="min-w-[200px]"
                  checked={skuSelected?.includes(sku.skuId)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onSKUChange([...skuSelected, sku.skuId]);
                    } else {
                      onSKUChange(skuSelected?.filter((id) => id !== sku.skuId) ?? []);
                    }
                  }}
                >
                  <Tooltip title={text} mouseEnterDelay={0.3}>
                    <div className="max-w-[250px] text-ellipsis whitespace-nowrap overflow-hidden">
                      {text}
                    </div>
                  </Tooltip>
                </Checkbox>
              </div>
              <Divider className="!my-0" />
            </>
          );
        })}
      </div>
      <div className="flex items-center px-[4px] gap-[4px] h-[30px] text-[12px] cursor-pointer">
        <Checkbox
          indeterminate={
            filteredSkuInfo.some((sku) => skuSelected?.includes(sku.skuId)) &&
            !filteredSkuInfo.every((sku) => skuSelected?.includes(sku.skuId))
          }
          checked={
            filteredSkuInfo.length > 0 &&
            filteredSkuInfo.every((sku) => skuSelected?.includes(sku.skuId))
          }
          onChange={(e) => {
            const filteredSkuIds = new Set(filteredSkuInfo.map((sku) => sku.skuId));
            if (e.target.checked) {
              const otherSelected = skuSelected?.filter((id) => !filteredSkuIds.has(id)) ?? [];
              onSKUChange([...otherSelected, ...filteredSkuInfo.map((sku) => sku.skuId)]);
            } else {
              onSKUChange(skuSelected?.filter((id) => !filteredSkuIds.has(id)) ?? []);
            }
          }}
        />
        <span>{t('全选')}</span>
        <Divider type="vertical" />
        <span>
          {t('已选')} {skuSelected?.length || 0}/{skuInfo?.length || 0}
        </span>
      </div>
    </div>
  );
};

const ApplyAllV2 = (props: ApplyAllV2Props) => {
  const { skuInfo = [], active, onClick } = props;
  const { t } = useTranslate();

  return (
    <Popover
      trigger="click"
      content={
        <div>
          {skuInfo.map((sku) => {
            const text = `${sku.sellerSku || '-'}${sku.skuName ? `(${sku.skuName})` : ''}`;
            return (
              <>
                <div
                  key={sku.skuId}
                  className="flex items-center px-[4px] gap-[4px] h-[30px] text-[12px] cursor-pointer"
                >
                  <Checkbox disabled className="min-w-[200px]" checked={true}>
                    <Tooltip title={text} mouseEnterDelay={0.3}>
                      <div className="max-w-[250px] text-ellipsis whitespace-nowrap overflow-hidden">
                        {text}
                      </div>
                    </Tooltip>
                  </Checkbox>
                </div>
                <Divider className="!my-0" />
              </>
            );
          })}
          <div className="flex items-center px-[4px] gap-[4px] h-[30px] text-[12px] cursor-pointer">
            <Checkbox checked={true} disabled />
            <span>{t('全选')}</span>
            <Divider type="vertical" />
            <span>
              {t('已选')} {skuInfo.length}/{skuInfo.length}
            </span>
          </div>
        </div>
      }
      placement="right"
      overlayClassName={overlay}
    >
      <div
        className={cx('flex items-center px-[4px] h-[30px] text-[12px] cursor-pointer', {
          'text-[#336fff]': active,
        })}
        onClick={() => onClick()}
      >
        <Radio checked={active} />
        <span>{t('应用于商品链接下所有SKU')}</span>
        <RightOutlined className="text-[10px] ml-2" />
      </div>
    </Popover>
  );
};

export const ApplyProduct = (props: ApplyProductProps) => {
  const {
    size = 'medium',
    showImage = true,
    imageUrl,
    skuInfo = [],
    skuSelected,
    selectable,
    defaultApplyType = 'link',
    onSKUChange,
    onClick,
    v2 = false,
    onHide,
  } = props;
  const { t } = useTranslate();
  const [applyType, setApplyType] = useState<'link' | 'sku'>(defaultApplyType);
  const [menuVisible, setMenuVisible] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLDivElement>(null);

  const menu = (
    <div ref={menuRef}>
      {v2 ? (
        <ApplyAllV2
          active={applyType === 'link'}
          skuInfo={skuInfo}
          onClick={() => {
            onSKUChange([]);
            setApplyType('link');
          }}
        />
      ) : (
        <div
          className={cx('flex items-center px-[4px] h-[30px] text-[12px] cursor-pointer', {
            'text-[#336fff]': applyType === 'link',
          })}
          onClick={() => {
            onSKUChange([]);
            setApplyType('link');
          }}
        >
          <Radio checked={applyType === 'link'} />
          <span>{t('应用于商品链接下所有SKU')}</span>
        </div>
      )}
      <Divider className="!my-0" />
      <Popover
        trigger="click"
        content={
          <div ref={selectRef} className="min-w-[200px]">
            <SKUGrid skuInfo={skuInfo} skuSelected={skuSelected} onSKUChange={onSKUChange} />
          </div>
        }
        placement="right"
        overlayClassName={overlay}
      >
        <div
          className={cx('flex items-center px-[4px] h-[30px] text-[12px] cursor-pointer', {
            'text-[#336fff]': applyType === 'sku',
          })}
          onClick={() => {
            setApplyType('sku');
          }}
        >
          <Radio checked={applyType === 'sku'} />
          <span>
            {t('应用于链接下部分SKU')}({skuSelected?.length || 0}/{skuInfo?.length || 0})
          </span>
          <RightOutlined className="text-[10px] ml-2" />
        </div>
      </Popover>
    </div>
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (popoverRef.current && popoverRef.current.contains(event.target as Node)) ||
        (menuRef.current && menuRef.current.contains(event.target as Node)) ||
        (selectRef.current && selectRef.current.contains(event.target as Node))
      ) {
        setMenuVisible(true);
      } else {
        setMenuVisible(false);
        onHide?.(applyType);
      }
    };

    if (menuVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuVisible, applyType, onHide]);

  return (
    <div
      ref={popoverRef}
      className={cx('p-1 flex items-center gap-2 justify-end group', {
        'p-0': size === 'small',
      })}
      onClick={onClick}
    >
      <Popover visible={menuVisible} content={menu} placement="bottom" overlayClassName={overlay}>
        <div
          className={cx('flex items-center gap-1 cursor-pointer', {
            'text-[#A7A7A7]': !selectable,
            'text-[#336fff]': selectable,
            'group-hover:text-[#336fff]': selectable,
          })}
          onClick={() => {
            if (!selectable) {
              return;
            }
            setMenuVisible(!menuVisible);
          }}
        >
          {applyType === 'link'
            ? t('应用于商品链接下所有SKU')
            : `${t('应用于链接下部分SKU')}(${skuSelected?.length || 0}/${skuInfo?.length || 0})`}
          <DownOutlined />
        </div>
      </Popover>
      {showImage && (
        <img
          className={
            'w-[32px] h-[32px] rounded-sm group-hover:border group-hover:border-solid group-hover:border-[#336fff]'
          }
          src={imageUrl}
          alt="product"
        />
      )}
    </div>
  );
};

const overlay = css`
  .ant-popover-inner-content {
    padding: 0;
  }
`;
